import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import axios from "axios";
// import PictureEditor from "../PictureEditor"; // Uncomment if using
import ConfirmDialog from "../common/ComfirmationDialog";
import PictureEditor from "../PictureEditor";

const ServiceManagement = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, name, ...}
  const [deleting, setDeleting] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Load services from backend
  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/SystemServices`,
        { withCredentials: true }
      );
      setServices(res.data.data);
    } catch (e) {
      setSnack({ open: true, message: e.message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line
  }, []);

  // Open dialog for add/edit
  const handleOpenDialog = (service = null) => {
    setEditing(service);
    if (service) {
      setForm(service);
    } else {
      setForm({ name: "", logo_url: "", description: "" });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditing(null);
    setForm({ name: "", description: "" });
  };

  // Create or update
  const handleSave = async () => {
    try {
      if (!form.name || !form.description) {
        setSnack({
          open: true,
          message: "Name & description required",
          severity: "warning",
        });
        return;
      }
      setIsSubmit(true);
      if (editing) {
        // Update
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/SystemServices/${editing.id}`,
          form
        );
        setSnack({
          open: true,
          message: "Service updated",
          severity: "success",
        });
      } else {
        // Create
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/SystemServices`,
          form
        );
        setSnack({
          open: true,
          message: "Service created",
          severity: "success",
        });
      }
      fetchServices();
      handleCloseDialog();
    } catch (e) {
      setSnack({ open: true, message: e.message, severity: "error" });
    } finally {
      setIsSubmit(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/SystemServices/${deleting.id}`
      );
      setSnack({ open: true, message: "Service deleted", severity: "success" });
      fetchServices();
      setDeleting(null);
      setDeleteDialogOpen(false);
    } catch (e) {
      setSnack({ open: true, message: e.message, severity: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLogoChange = async (service, newUrl) => {
    setIsSubmit(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/SystemServices/${service.id}`,
        { logo_url: newUrl }
      );
      fetchServices();
    } catch (e) {
      setSnack({ open: true, message: e.message, severity: "error" });
    } finally {
      setIsSubmit(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="right" mb={2}>
        <Button
          startIcon={<AddCircleOutlineIcon />}
          variant="contained"
          onClick={() => handleOpenDialog()}
        >
          Add Service
        </Button>
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            overflow: "auto",
            maxHeight: "50vh",
            minWidth: "100%",
            border: "1px",
            borderBlockEndColor: "#cbd5e1",
            backgroundColor: "transparent",
          }}
        >
          <Grid
            container
            spacing={3}
            columns={{ xs: 12, sm: 12, md: 12 }}
            sx={{ m: 3 }}
          >
            {services.map((service) => (
              <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={service.id}>
                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow: 6,
                    position: "relative",
                    transition: "transform 0.2s",
                    "&:hover": { transform: "scale(1.02)" },
                    display: "flex",
                    flexDirection: "column",
                    height: "180px",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} p={2}>
                    {/* Uncomment if using PictureEditor, otherwise use Avatar */}
                    <PictureEditor
                      initialImage={service.logo_url}
                      type="rounded"
                      onImageChange={(newUrl) =>
                        handleLogoChange(service, newUrl)
                      }
                      size={60}
                    />
                    <Typography variant="h6" fontWeight={600} noWrap>
                      {service.name}
                    </Typography>
                  </Box>
                  <CardContent sx={{ pt: 0 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {service.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ position: "absolute", right: 8, top: 8 }}>
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(service)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        onClick={() => {
                          setDeleting(service);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
            {services.length === 0 && (
              <Grid item xs={12}>
                <Typography
                  color="text.secondary"
                  align="center"
                  sx={{ mt: 4 }}
                >
                  No services available.
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editing ? "Edit Service" : "Add New Service"}
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 2,
            mb: 2,
          }}
        >
          <TextField
            label="Service Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            fullWidth
            sx={{ mt: 2 }}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) =>
              setForm((f) => ({ ...f, description: e.target.value }))
            }
            multiline
            minRows={2}
            fullWidth
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.name || !form.description || isSubmit}
          >
            {editing
              ? isSubmit
                ? "Saving"
                : "Save Changes"
              : isSubmit
              ? "Adding"
              : "Add Service"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Confirm"
        content={
          <span>
            Are you sure you want to delete <b>{deleting?.name}</b>?
          </span>
        }
        loading={isDeleting}
      />
      <Snackbar
        open={snack.open}
        autoHideDuration={2800}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ServiceManagement;
